import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mic, MoreVertical, Volume2 } from 'lucide-react';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { translateFallback, translateText } from '../hooks/useTranslation';
import { searchWord } from '../services/dictionary';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../store/useStore';
import { PracticeModal } from './PracticeModal';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { getBrazilianPhoneticGuide } from '../utils/brazilianPhonetics';

interface WordCardProps {
  word: string;
  definition?: string;
  pronunciation?: string;
  progress?: number;
  lastPractice?: string;
  fromTheme?: boolean;
  themeName?: string;
  showDelete?: boolean;
  disableFlip?: boolean;
  sourceLanguage?: string;
  isExpression?: boolean;
  type?: string;
}

export default function WordCard({ word, lastPractice, pronunciation, progress, showDelete, fromTheme, themeName, disableFlip, sourceLanguage, isExpression, type }: WordCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const { speak } = useSpeechSynthesis({ lang: 'en-US' });
  const [translation, setTranslation] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { deleteWord, deleteExpression, updateWordProgress } = useContext(UserContext);

  const [isPracticing, setIsPracticing] = useState(false);
  const { isRecording, transcript, startRecording, stopRecording } = useSpeechRecognition({});
  const [practiceState, setPracticeState] = useState({
    isRecording: false,
    transcription: '',
    audioBlob: null,
    attempts: 0,
    score: null,
  });

  useEffect(() => {
    setPracticeState(prev => ({
      ...prev,
      isRecording,
      transcription: transcript,
    }));
  }, [isRecording, transcript]);

  const handleStartRecording = () => {
    startRecording();
    setPracticeState(prev => ({ ...prev, attempts: prev.attempts + 1 }));
    updateWordProgress(word, themeName);
  };

  const handleClosePractice = () => {
    setIsPracticing(false);
    stopRecording();
    setPracticeState({
      isRecording: false,
      transcription: '',
      audioBlob: null,
      attempts: 0,
      score: null,
    });
  };

  const getTranslation = async () => {
    let textToTranslate = word;
    if (isExpression) {
      try {
        const data = await searchWord(word);
        const firstMeaning = data?.meanings?.[0];
        if (firstMeaning && firstMeaning.definitions && firstMeaning.definitions.length > 0) {
          const firstDef = firstMeaning.definitions[0];
          textToTranslate = typeof firstDef === 'string' ? firstDef : firstDef.definition;
        }
      } catch (e) {
        console.error('Error fetching expression definition for translation:', e);
      }
    }
    
    const cleanedText = isExpression ? textToTranslate : textToTranslate.replace(/[^\w\s]/gi, '');
    const translationResult = await translateText(cleanedText, 'en', 'pt');
    if (translationResult.toLowerCase().includes(cleanedText.toLowerCase())) {
      translateFallback(cleanedText).then((result) => {
        setTranslation(result);
      });
    } else {
      setTranslation(translationResult);
    }
  }

  const handleFlipCard = () => {
    if(!disableFlip) {
      setIsFlipped(!isFlipped);
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    if(word && isFlipped)
      getTranslation()
  }, [isFlipped])

  return (
    <>
    <motion.div
      className="relative w-full h-48 cursor-pointer hover:shadow-2xl rounded-full hover:bg-gray-400"
      onClick={handleFlipCard}
      initial={false}
      animate={{ rotateY: isFlipped ? 180 : 0 }}
      transition={{ duration: 0.6, type: 'spring' }}
      style={{ transformStyle: 'preserve-3d' }}
      data-testid="word-card"
    >
      <div
        className="absolute w-full h-full rounded-xl p-6 bg-white shadow-lg border border-gray-200"
        style={{ backfaceVisibility: 'hidden' }}
      >
        <div className="flex justify-between items-start">
          <div className="flex flex-col">
            {type && type !== 'single_word' && (
              <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide mb-1 self-start">
                {type.replace('_', ' ')}
              </span>
            )}
            <h3 className="text-2xl font-bold text-gray-800">{word}</h3>
          </div>
          <div className="flex items-center space-x-2">
            { word != undefined && sourceLanguage=== 'pt' && (<button
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="pronunciation"
              onClick={(e) => {
                e.stopPropagation();
                speak(word);
              }}
            >
              <Volume2 className="w-5 h-5 text-gray-600" />
            </button>)}
            {showDelete && <div className="relative">
              <button
                className="p-2 hover:bg-gray-100 rounded-full"
                data-testid="menu"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(!menuOpen);
                }}
              >
                <MoreVertical className="w-5 h-5 text-gray-600" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <button
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isExpression && deleteExpression) {
                        deleteExpression(word);
                      } else {
                        deleteWord(word);
                      }
                      setMenuOpen(false);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div> }
          </div>
        </div>
        {lastPractice && <p className="text-sm text-gray-500" data-testid="last-practice">
          Last practiced: {new Intl.DateTimeFormat('pt-BR').format(new Date(lastPractice))}
        </p>}
        {word && disableFlip && sourceLanguage=== 'pt' && (
            <div className="flex flex-row gap-2 items-center justify-center mt-4">
            <button
              onClick={() => navigate(isExpression ? '/expressions' : '/vocabulary', { state: { word, fromTheme, themeName } })}
              aria-label='See more details'
              className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors duration-300"
            >
              See More Details
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isExpression) {
                  setIsPracticing(true);
                } else {
                  navigate('/speaking', { state: { word, fromTheme, themeName } });
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-400 transition-colors duration-300"
            >
              <Mic className="w-4 h-4" />
              Practice
            </button>
          </div>
          )}
        {word && (() => {
          const guide = getBrazilianPhoneticGuide(word, pronunciation);
          return (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-mono text-gray-500 font-medium">
                {guide.ipa}
              </span>
              <span className="text-[11px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200" title="Como pronunciar em português">
                Falar: "{guide.respelling}"
              </span>
            </div>
          );
        })()}
        { progress != undefined && (<div className="absolute bottom-5 left-6 right-6">
          <div className="flex justify-between items-center mb-1.5">
             <span className={`text-xs font-bold ${progress * 20 < 33 ? 'text-red-500' : progress * 20 < 66 ? 'text-yellow-500' : 'text-green-500'}`}>Progress</span>
             <span className={`text-xs font-bold ${progress * 20 < 33 ? 'text-red-500' : progress * 20 < 66 ? 'text-yellow-500' : 'text-green-500'}`}>{Math.min(100, progress * 20)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              data-testid="progress-bar"
              className={`h-2 rounded-full transition-all duration-300 ${
                progress * 20 < 33
                  ? 'bg-red-500'
                  : progress * 20 < 66
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${progress * 20 > 100 ? 100 : progress * 20}%` }}
            />
          </div>
        </div>)}
      </div>

      <div
        className="absolute w-full h-full rounded-xl p-6 bg-white shadow-lg border border-gray-200"
        style={{
          transform: 'rotateY(180deg)',
          backfaceVisibility: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <div className="flex flex-col gap-2">
          {translation && <p className="text-2xl font-bold text-gray-800" data-testid="translation">{translation}</p>}
          {!translation && (
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" />
            </div>
          )}
          <div className="flex flex-row gap-2 items-center justify-center mt-4">
            <button
              onClick={() => navigate(isExpression ? '/expressions' : '/vocabulary', { state: { word, fromTheme, themeName } })}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors duration-300"
            >
              See More Details
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isExpression) {
                  setIsPracticing(true);
                } else {
                  navigate('/speaking', { state: { word, fromTheme, themeName } });
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-400 transition-colors duration-300"
            >
              <Mic className="w-4 h-4" />
              Practice
            </button>
          </div>
        </div>
      </div>
    </motion.div>
    {isPracticing && (
      <PracticeModal
        example={{ text: word, partOfSpeech: type || 'phrase' }}
        practiceState={practiceState}
        onClose={handleClosePractice}
        onStartRecording={handleStartRecording}
        onStopRecording={stopRecording}
        isRecording={isRecording}
      />
    )}
    </>
  );
}